import { z } from "zod";
import {
  asAmount,
  asBondPriceNominal,
  asCorporateBondId,
  asCouponPayment,
  asIssuerId,
  asYield,
} from "@goodfill/domain-properties";
import { zISODateTime, zDecimal, zMoney } from "@goodfill/domain-properties-zod-validators";
import { zBondRatings } from "../zodBondRatings";
import { applyWithOptionalToNull } from "@goodfill/map";

export const mockedOrderActionSchema = z.union([
  z.literal("fill"),
  z.literal("wait"),
  z.literal("cancel"),
  z.literal("reject"),
]);

export type MockedOrderAction = z.infer<typeof mockedOrderActionSchema>;

export const corporateBondProfileSchema = z.object({
  bondIdCusip: z.string().transform(asCorporateBondId),
  maturityDate: zISODateTime(),
  isin: z.string(),
  issuerId: z.string().transform(asIssuerId),
  issuerName: z.string(),
  issuerTicker: z.string(),
  couponPayment: zDecimal().transform(asCouponPayment),
  outstandingAmount: zMoney(),
  minimumPiece: zMoney(),
  minimumIncrement: zMoney(),
  paymentRank: z.string(),
  industry: z.string(),
  esgScore: z.string(),
  makeWholeCall: z.union([z.null(), z.string()]),
  ratings: zBondRatings,
  isOfferedToCustomers: z.boolean(),
  numberOfHoldingCustomers: z.number(),

  // ask (customer buy)
  askPrice: zDecimal().optional().nullable().transform(applyWithOptionalToNull(asBondPriceNominal)),
  askYield: zDecimal().optional().nullable().transform(applyWithOptionalToNull(asYield)),
  askAmount: zDecimal().optional().nullable().transform(applyWithOptionalToNull(asAmount)),

  // bid (customer sell)
  bidPrice: zDecimal().optional().nullable().transform(applyWithOptionalToNull(asBondPriceNominal)),
  bidYield: zDecimal().optional().nullable().transform(applyWithOptionalToNull(asYield)),
  bidAmount: zDecimal().optional().nullable().transform(applyWithOptionalToNull(asAmount)),

  mockedOrderAction: z.preprocess((a) => (a === null ? "fill" : a), mockedOrderActionSchema),
});

export type CorporateBondProfile = z.infer<typeof corporateBondProfileSchema>;
